import React, { useRef, useState, useEffect } from "react";
import { Upload } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

export default function EmiratesIdUpload({
  id,
  label,
  file,
  setFile,
  maxSize = 5 * 1024 * 1024,
  translateError,
  existingImageUrl, // New prop for existing image
}) {
  const { translate } = useLanguage();
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [imageLoadError, setImageLoadError] = useState(false);
  useEffect(() => {
    
    if (!file && existingImageUrl) {
      setPreview(existingImageUrl);
      setImageLoadError(false);
    }
  }, [existingImageUrl, file]);


  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size > maxSize) {
      setFile(null);
      setPreview(null);
      setError(translateError || `File must be less than ${maxSize / (1024 * 1024)}MB`);
      return;
    }

    setFile(selectedFile);
    setError("");
    setImageLoadError(false);

    // Create preview for image files
    const reader = new FileReader();
    reader.onload = (event) => {
      setPreview(event.target.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const openFileDialog = () => {
    if (inputRef.current) inputRef.current.click();
  };

  const formatFileSize = (size) => {
    if (size < 1024) return size + " B";
    if (size < 1024 * 1024) return (size / 1024).toFixed(2) + " KB";
    return (size / (1024 * 1024)).toFixed(2) + " MB";
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-white mb-2">{label}</label>

      <div className="border-2 border-dashed border-gray-500 rounded-lg p-4 text-center">
        {/* Hidden input */}
        <input
          ref={inputRef}
          type="file"
          id={id}
          onChange={handleFileChange}
          className="hidden"
          accept=".jpg,.jpeg,.png,.pdf"
        />

        {/* Upload icon and text */}
        <div className="flex flex-col items-center">
          <Upload className="w-6 h-6 text-gray-500 mb-2" />
          <p className="text-sm text-gray-500 text-center">
            {translate("dashboard.addUser.upload.clickBrowse") || 'Click "Browse File"'} <br />
            {translate("dashboard.addUser.upload.supportedFormats") || "Supported: JPEG, PNG, JPG, PDF | Max size:"} {maxSize / (1024 * 1024)}MB
          </p>

          {/* Button */}
          <button
            type="button"
            onClick={openFileDialog}
            className="mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            {translate("dashboard.addUser.upload.browseFile") || "Browse File"}
          </button>

          {preview && !imageLoadError && (
            <div className="mt-4 w-full">
              <p className="text-xs text-gray-600 mb-2 font-medium">
                {file ? "New Image" : "Current Image"}
              </p>
              <img
                src={preview}
                alt="Preview"
                className="w-full h-32 object-cover rounded-lg border border-gray-300"
                onError={() => setImageLoadError(true)}
              />
            </div>
          )}


          {/* Selected file name */}
          {file && (
            <p className="mt-2 text-sm text-green-600">
              {file.name} ({formatFileSize(file.size)})
            </p>
          )}
        </div>

        {/* Error message */}
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      </div>
    </div>
  );
}
